import {IntlShape, defineMessages} from 'react-intl'

/**
 * Strings shared by the three notification dropdowns in the navbar
 * (`HeaderGeneralNotificationsMenu`, `HeaderLeadNotificationsMenu` and
 * `HeaderSignatureNotificationsMenu`). The three are near-identical copies, so
 * a single catalog keeps them from drifting apart in translation.
 *
 * The per-flavour empty states live in each component, since their wording
 * names the notification kind.
 */
export const NOTIFICATION_MESSAGES = defineMessages({
  // Header
  title: {id: 'shell.notifications.title', defaultMessage: 'Notifications'},
  unreadCount: {id: 'shell.notifications.unreadCount', defaultMessage: '{count} unread'},
  allRead: {id: 'shell.notifications.allRead', defaultMessage: 'All read'},

  // Tabs
  tabAll: {id: 'shell.notifications.tab.all', defaultMessage: 'All ({count})'},
  tabUnread: {id: 'shell.notifications.tab.unread', defaultMessage: 'Unread ({count})'},
  tabRead: {id: 'shell.notifications.tab.read', defaultMessage: 'Read ({count})'},

  // Actions
  markAllRead: {id: 'shell.notifications.markAllRead', defaultMessage: 'Mark all as read'},
  viewAll: {id: 'shell.notifications.viewAll', defaultMessage: 'View All'},
  pipeline: {id: 'shell.notifications.pipeline', defaultMessage: 'Pipeline: {name}'},
  createdBy: {id: 'shell.notifications.createdBy', defaultMessage: 'By {name}'},
  deleteNotification: {
    id: 'shell.notifications.delete',
    defaultMessage: 'Delete notification',
  },

  // Priority — DB enum rendered as a label (§10 del plan): the English value
  // still travels to the API, only the label is translated.
  priorityUrgent: {id: 'shell.notifications.priority.urgent', defaultMessage: 'URGENT'},
  priorityHigh: {id: 'shell.notifications.priority.high', defaultMessage: 'HIGH'},
  priorityNormal: {id: 'shell.notifications.priority.normal', defaultMessage: 'NORMAL'},
  priorityLow: {id: 'shell.notifications.priority.low', defaultMessage: 'LOW'},

  // Toasts
  deleted: {id: 'shell.notifications.toast.deleted', defaultMessage: 'Notification deleted'},
  markedAsRead: {
    id: 'shell.notifications.toast.markedAsRead',
    defaultMessage: '{count} notifications marked as read',
  },
  errorLoading: {
    id: 'shell.notifications.toast.errorLoading',
    defaultMessage: 'Error loading notifications: {message}',
  },
  errorMarkingRead: {
    id: 'shell.notifications.toast.errorMarkingRead',
    defaultMessage: 'Error marking notification as read: {message}',
  },
  errorMarkingAllRead: {
    id: 'shell.notifications.toast.errorMarkingAllRead',
    defaultMessage: 'Error marking all as read: {message}',
  },
  errorDeleting: {
    id: 'shell.notifications.toast.errorDeleting',
    defaultMessage: 'Error deleting notification: {message}',
  },
})

/**
 * Maps the raw `priority` enum coming from the backend to a translated badge
 * label. Unknown values fall through untouched so a new backend priority still
 * renders something instead of disappearing.
 */
export const priorityLabel = (intl: IntlShape, priority: string): string => {
  switch (priority) {
    case 'URGENT':
      return intl.formatMessage(NOTIFICATION_MESSAGES.priorityUrgent)
    case 'HIGH':
      return intl.formatMessage(NOTIFICATION_MESSAGES.priorityHigh)
    case 'NORMAL':
      return intl.formatMessage(NOTIFICATION_MESSAGES.priorityNormal)
    case 'LOW':
      return intl.formatMessage(NOTIFICATION_MESSAGES.priorityLow)
    default:
      return priority
  }
}
